import useHelpTarget from '../hooks/useHelpTarget';

/**
 * TranscriptUpgradePanel
 *
 * The inline "what you have now → what you'd get" comparison shown when the
 * user SELECTS a not-yet-run transcript source from the source rail (Full
 * coverage via Google, or AI transcript via AssemblyAI). Replaces the old
 * always-on ImproveTranscriptCard banner — the prompt now appears only on
 * deliberate selection, and never sells a transcript for media with no speech.
 *
 * Pure presentational: the parent owns selection + the actual generate
 * handlers (Google = handleFullTranscribe; AssemblyAI = consent modal).
 *
 * Props:
 *   provider    'google' | 'assemblyai'
 *   nowSummary  string — live "what you have now" line
 *   hasSpeech   bool — false ⇒ nothing to transcribe (disable + explain)
 *   running     bool — this upgrade is queued/in flight
 *   onGenerate  () => void
 *   onClose     () => void
 */
const META = {
  google: {
    icon: '☁',
    name: 'Full coverage (Google)',
    heading: 'Transcribe the full audio with Google',
    gains: [
      '100% coverage — every spoken second, including quiet / sparse passages',
      'Re-runs analysis + entity & graph extraction on the complete text',
      'Stays inside Google Assured Workloads — no data leaves, no per-minute charge',
    ],
    cost: null,
    boundary: null,
    cta: '☁ Generate full transcript',
    running: '☁ Running…',
  },
  assemblyai: {
    icon: '✨',
    name: 'AI transcript (AssemblyAI)',
    heading: 'Upgrade this transcript with AssemblyAI',
    gains: [
      'Speaker labels — who said what, turn by turn',
      'Auto-chapters — topic breaks for fast navigation',
      'Named-entity detection — people, orgs, places',
      'Higher accuracy on noisy / overlapping / low-volume audio',
      'Becomes your unified Full Transcript',
    ],
    cost: '~$0.85/min',
    boundary: '⚠ Audio leaves Google Assured Workloads — you’ll confirm first',
    cta: '✨ Generate AI transcript',
    running: '✨ Generating…',
  },
};

export default function TranscriptUpgradePanel({
  provider, nowSummary, hasSpeech, running, onGenerate, onClose,
}) {
  const m = META[provider];
  const helpRef = useHelpTarget({
    slug: 'transcripts-and-speaker-labels',
    label: 'Transcript upgrade options',
    question: "What's the difference between Full coverage and AI transcript?",
  });
  if (!m) return null;

  return (
    <div className={`tup tup--${provider}`} ref={helpRef}>
      <div className="tup-head">
        <span className="tup-title">{m.icon} {m.heading}</span>
        <button className="tup-close" type="button" onClick={onClose} title="Close">✕</button>
      </div>

      {!hasSpeech ? (
        <div className="tup-nospeech">
          This file has no detected speech — a spoken-word transcript won’t add
          content. The Visual timeline already describes the scene.
        </div>
      ) : (
        <>
          <div className="tup-now">
            <span className="tup-now-label">Now</span>
            <span className="tup-now-val">{nowSummary}</span>
          </div>
          <div className="tup-get">
            <span className="tup-get-label">You’d get</span>
            <ul className="tup-gains">
              {m.gains.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          </div>
          {(m.cost || m.boundary) && (
            <div className="tup-meta">
              {m.cost && <span className="tup-cost">{m.cost}</span>}
              {m.boundary && <span className="tup-boundary">{m.boundary}</span>}
            </div>
          )}
        </>
      )}

      <div className="tup-actions">
        <button
          className={`btn btn-sm ${provider === 'assemblyai' ? 'btn-primary' : ''}`}
          type="button"
          onClick={onGenerate}
          disabled={!hasSpeech || running}
        >
          {running ? m.running : m.cta}
        </button>
      </div>
    </div>
  );
}
